/**
 * Reduz uma dataURL pra caber no localStorage.
 * - Logos: max 512px, ~50KB
 * - Avatares/spokesperson: max 768px, ~150KB
 * - Imagens de demanda: max 1280px, ~300KB
 */
export async function resizeImageDataUrl(
  dataUrl: string,
  maxSide: number,
  quality = 0.85,
  forceFormat?: "image/jpeg" | "image/png" | "image/webp"
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("canvas indisponível"));
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, w, h);

        // PNG preserva transparência (importante pra logos)
        const isTransparent = dataUrl.startsWith("data:image/png") || dataUrl.startsWith("data:image/svg");
        const format = forceFormat ?? (isTransparent ? "image/png" : "image/jpeg");
        const out = canvas.toDataURL(format, quality);

        // Se ficou maior que o original (raro), retorna original
        resolve(out.length < dataUrl.length ? out : dataUrl);
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error("falha ao carregar imagem"));
    img.src = dataUrl;
  });
}

export const resizeForLogo       = (d: string) => resizeImageDataUrl(d, 512,  0.9);
export const resizeForSpokesperson = (d: string) => resizeImageDataUrl(d, 768,  0.82, "image/jpeg");
export const resizeForAvatar     = (d: string) => resizeImageDataUrl(d, 384,  0.82, "image/jpeg");
export const resizeForCreative   = (d: string) => resizeImageDataUrl(d, 1280, 0.85);
export const resizeForReference  = (d: string) => resizeImageDataUrl(d, 1024, 0.8,  "image/jpeg");

/** Estima tamanho aproximado de uma dataURL em bytes. */
export function estimateDataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(",");
  if (comma === -1) return dataUrl.length;
  return Math.floor((dataUrl.length - comma - 1) * 0.75);
}

/** Tamanho total ocupado por uma key do localStorage. */
export function localStorageKeySizeBytes(key: string): number {
  const v = localStorage.getItem(key);
  return v ? new Blob([v]).size : 0;
}

/** Tamanho total do localStorage da origem. */
export function localStorageTotalSizeBytes(): number {
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k) continue;
    const v = localStorage.getItem(k);
    total += new Blob([k + (v ?? "")]).size;
  }
  return total;
}

export function humanBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
